import { zodResolver } from "@hookform/resolvers/zod";
import {
	Alert,
	AlertDescription,
	Button,
	Checkbox,
	cn,
	Input,
	Label,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Separator,
} from "@monbits/ui";
import { InfoIcon } from "lucide-react";
import {
	forwardRef,
	useEffect,
	useId,
	useImperativeHandle,
	useState,
} from "react";
import { useForm } from "react-hook-form";
import AddressCreateForm from "src/components/address-create-form/address-create-form";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import DrawerForm from "src/components/drawer-form/drawer-form";
import SpinWithMask from "src/components/spin-with-mask/spin-with-mask";
import addressQueries from "src/queries/address-queries";
import type {
	MerchantAddress,
	MerchantAddressParams,
	MerchantBalance,
	WithdrawalCreateRequest,
} from "src/types";
import { sleep } from "src/utils";
import { formatCryptoAmount, splitTicker } from "src/utils/format";
import { z } from "zod";

import {
	useAvailableBalance,
	useConvertedAmount,
} from "./hooks/use-exchange-rate";
import { useServiceFee } from "./hooks/use-service-fee";
import {
	checkCorrectAmount,
	getWithdrawalValidation,
	normalizeAmount,
	renderRecipientAmount,
	renderTotalConvertedAmount,
	renderTotalSum,
} from "./hooks/use-withdrawal-validation";

interface Props {
	balance: MerchantBalance;
	onCancel: () => void;
	onFinish: (values: WithdrawalCreateRequest) => Promise<void>;
	addresses: MerchantAddress[];
	openPopupFunc: (title: string, description: string) => void;
	isFormSubmitting: boolean;
}

const withdrawalFormSchema = z.object({
	addressId: z.string().min(1, "Please select an address"),
	amount: z
		.string()
		.min(1, "Please enter an amount")
		.refine((value) => checkCorrectAmount(value), {
			message: "Please enter a valid amount",
		}),
	deductFeeFromAmount: z.boolean(),
});

type WithdrawalFormValues = z.infer<typeof withdrawalFormSchema>;

export type WithdrawFormHandle = {
	submit: () => Promise<void>;
};

const WithdrawForm = forwardRef<WithdrawFormHandle, Props>((props, ref) => {
	const addressSelectId = useId();
	const amountInputId = useId();
	const [isAddressFormOpen, setIsAddressFormOpen] = useState(false);

	const form = useForm<WithdrawalFormValues>({
		resolver: zodResolver(withdrawalFormSchema),
		defaultValues: {
			addressId: "",
			amount: "",
			deductFeeFromAmount: false,
		},
		mode: "onChange",
	});
	const {
		handleSubmit,
		reset,
		setValue,
		setError,
		clearErrors,
		watch,
		formState,
	} = form;

	const amountValue = watch("amount");
	const addressValue = watch("addressId");
	const deductFeeFromAmount = watch("deductFeeFromAmount");

	const isAmountValid = checkCorrectAmount(amountValue);
	const normalizedAmount = normalizeAmount(amountValue, props.balance.amount);

	const createAddress = addressQueries.createAddress();

	const serviceFeeQuery = useServiceFee(
		props.balance.id !== "empty" ? props.balance.id : undefined,
	);

	const availableBalanceQuery = useAvailableBalance(
		props.balance.ticker,
		props.balance.amount,
		props.balance.isTest,
		props.balance.id !== "empty",
	);

	const fee = serviceFeeQuery.data;
	const availableBalance = availableBalanceQuery.data || "";

	const convertedAmountQuery = useConvertedAmount(
		props.balance.ticker,
		normalizedAmount,
		isAmountValid,
	);

	const convertedAmount = convertedAmountQuery.data || "";

	const {
		hasValidAmount,
		hasInsufficientBalance,
		hasAmountBelowMinimum,
		deductNetNotPositive,
	} = getWithdrawalValidation(
		amountValue,
		props.balance,
		fee,
		convertedAmount,
		deductFeeFromAmount,
	);

	const { token, blockchain } = splitTicker(props.balance.ticker);
	const displayTicker = token || blockchain;
	const balanceId = `${props.balance.blockchainName} ${displayTicker}${
		props.balance.isTest ? " ⚠️ Testnet" : ""
	}`;

	const filteredAddresses = props.addresses.filter(
		(item) => item.blockchain === props.balance.blockchain,
	);

	const canSubmit =
		hasValidAmount &&
		!hasInsufficientBalance &&
		!hasAmountBelowMinimum &&
		!deductNetNotPositive &&
		Boolean(addressValue) &&
		!!fee;

	const isLoading =
		serviceFeeQuery.isLoading ||
		availableBalanceQuery.isLoading ||
		(isAmountValid && convertedAmountQuery.isLoading);

	// biome-ignore lint/correctness/useExhaustiveDependencies: false positve
	useEffect(() => {
		reset({
			addressId: "",
			amount: "",
		});
		setIsAddressFormOpen(false);
	}, [props.balance.id, reset]);

	const submitForm = handleSubmit(async (formValues) => {
		if (props.balance.amount === "empty" || !fee) {
			return;
		}

		if (!canSubmit) {
			if (hasAmountBelowMinimum) {
				setError("amount", {
					type: "manual",
					message: `Amount must be at least $${props.balance.minimalWithdrawalAmountUSD}`,
				});
			}
			if (!formValues.addressId) {
				setError("addressId", {
					type: "manual",
					message: "Please select an address",
				});
			}
			return;
		}

		const payload: WithdrawalCreateRequest = {
			balanceId: props.balance.id,
			addressId: formValues.addressId,
			amount: formValues.amount,
			deductFeeFromAmount: formValues.deductFeeFromAmount,
		};

		await props.onFinish(payload);
	});

	useImperativeHandle(
		ref,
		() => ({
			submit: () => submitForm(),
		}),
		[submitForm],
	);

	const uploadCreatedAddress = async (
		value: MerchantAddressParams,
		reset: () => void,
	) => {
		try {
			await createAddress.mutateAsync(value);
			setIsAddressFormOpen(false);
			props.openPopupFunc(
				"Address has created",
				`You have created new address ${value.name}`,
			);

			await sleep(1000);
			reset();
		} catch (error) {
			console.error("error occurred: ", error);
			throw error;
		}
	};

	const handleAddressChange = (value: string) => {
		setValue("addressId", value, { shouldValidate: true });
		clearErrors("addressId");
	};

	const handleAmountChange = (value: string) => {
		setValue("amount", value, { shouldValidate: true });
	};

	const handleAmountBlur = () => {
		const normalized = normalizeAmount(amountValue, props.balance.amount);
		if (normalized !== amountValue) {
			setValue("amount", normalized, { shouldValidate: true });
		}
	};

	const showSpinner = props.isFormSubmitting || isLoading;

	return (
		<>
			<form onSubmit={submitForm} className="space-y-6">
				<SpinWithMask isLoading={showSpinner} />

				{!serviceFeeQuery.isLoading &&
					Boolean(filteredAddresses.length) &&
					availableBalance && (
						<>
							<div className="bg-muted/30 rounded-lg p-6">
								<div className="flex items-center justify-between">
									<div className="space-y-1">
										<p className="text-sm text-muted-foreground">
											Available Balance
										</p>
										<div className="text-2xl font-bold">
											{formatCryptoAmount(props.balance.amount, {
												ticker: props.balance.ticker,
												maxDecimals: 6,
											})}{" "}
											<span className="text-lg text-muted-foreground">
												{displayTicker}
											</span>
										</div>
									</div>
									<div className="text-right">
										<p className="text-sm text-muted-foreground mb-1">
											USD Value
										</p>
										<div className="text-2xl font-bold text-(--trend-positive)">
											${availableBalance}
										</div>
									</div>
								</div>
							</div>

							<div className="space-y-2">
								<Label htmlFor={addressSelectId}>Selected Balance</Label>
								<div className="relative">
									<div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center">
										<CryptoIcon
											ticker={props.balance.ticker}
											className="h-5 w-5"
											showComposite={true}
										/>
									</div>
									<Input
										id={addressSelectId}
										value={balanceId}
										disabled
										className="pl-11"
									/>
								</div>
							</div>

							<div className="space-y-2">
								<Label htmlFor={addressSelectId}>
									Withdrawal Address
									<span className="text-destructive ml-1">*</span>
								</Label>
								<Select
									value={addressValue}
									onValueChange={handleAddressChange}
								>
									<SelectTrigger
										id={addressSelectId}
										className={cn("w-full", {
											"border-destructive": formState.errors.addressId,
										})}
									>
										<SelectValue placeholder="Select withdrawal address" />
									</SelectTrigger>
									<SelectContent>
										{filteredAddresses.map((address) => (
											<SelectItem key={address.id} value={address.id}>
												{address.name} ({address.address})
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								{formState.errors.addressId && (
									<p className="text-sm font-medium text-destructive">
										{formState.errors.addressId.message}
									</p>
								)}
							</div>

							<div className="space-y-2">
								<Label htmlFor={amountInputId}>
									Amount to Withdraw ({displayTicker})
									<span className="text-destructive ml-1">*</span>
								</Label>
								<Input
									id={amountInputId}
									type="number"
									step="0.0000001"
									min={0}
									max={parseFloat(props.balance.amount)}
									placeholder="0.00"
									value={amountValue}
									onChange={(event) =>
										handleAmountChange(event.currentTarget.value)
									}
									onBlur={handleAmountBlur}
									className={
										formState.errors.amount ? "border-destructive" : ""
									}
								/>
								{formState.errors.amount && (
									<p className="text-sm font-medium text-destructive">
										{formState.errors.amount.message}
									</p>
								)}
							</div>

							<div className="flex items-start gap-3">
								<Checkbox
									id="deductFeeFromAmount"
									checked={deductFeeFromAmount}
									onCheckedChange={(checked) =>
										setValue("deductFeeFromAmount", checked === true, {
											shouldValidate: true,
										})
									}
								/>
								<div className="space-y-1 leading-none">
									<Label
										htmlFor="deductFeeFromAmount"
										className="font-normal cursor-pointer"
									>
										Subtract service fee from this amount
									</Label>
									<p className="text-xs text-muted-foreground">
										{deductFeeFromAmount
											? "Recipient will receive this amount minus the service fee; your balance is debited exactly the entered amount."
											: "Service fee is charged on top: recipient gets the entered amount and your balance is debited amount + fee."}
									</p>
								</div>
							</div>

							{deductNetNotPositive && (
								<Alert variant="destructive">
									<AlertDescription className="text-xs">
										<strong>Amount too small.</strong> When subtracting the
										service fee, the recipient would receive zero or less.
										Increase the amount or uncheck the subtract option.
									</AlertDescription>
								</Alert>
							)}

							<div
								className={cn(
									"flex items-start gap-3 p-4 rounded-lg border",
									hasAmountBelowMinimum
										? "bg-destructive/10 border-destructive"
										: "bg-muted/50 border-border",
								)}
							>
								<InfoIcon
									className={cn(
										"h-5 w-5 flex-shrink-0 mt-0.5",
										hasAmountBelowMinimum
											? "text-destructive"
											: "text-muted-foreground",
									)}
								/>
								<div className="text-sm">
									<p
										className={
											hasAmountBelowMinimum
												? "text-destructive font-semibold"
												: "text-muted-foreground"
										}
									>
										{hasAmountBelowMinimum
											? "Amount below minimum: "
											: "Minimum withdrawal amount for "}
										{!hasAmountBelowMinimum && (
											<>
												<code className="font-semibold">{displayTicker}</code>{" "}
												is{" "}
											</>
										)}
										<span className="font-semibold">
											${props.balance.minimalWithdrawalAmountUSD}
										</span>
									</p>
								</div>
							</div>

							{hasValidAmount && fee && (
								<div className="space-y-4">
									<h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
										Transaction Summary
									</h4>
									<div className="space-y-3 p-4 rounded-lg bg-muted/30 border border-border">
										<div className="flex justify-between items-center">
											<span className="text-sm text-muted-foreground">
												Recipient receives
											</span>
											<div className="text-right">
												<span className="text-sm font-semibold">
													{formatCryptoAmount(
														deductFeeFromAmount
															? renderRecipientAmount(
																	amountValue,
																	props.balance,
																	fee,
																)
															: amountValue,
														{
															ticker: props.balance.ticker,
															maxDecimals: 6,
														},
													)}{" "}
													<code className="text-xs">{displayTicker}</code>
												</span>
												<span className="text-xs text-muted-foreground ml-2">
													≈${props.balance.isTest ? "0" : convertedAmount}
												</span>
											</div>
										</div>

										<div className="flex justify-between items-center">
											<span className="text-sm text-muted-foreground">
												{deductFeeFromAmount
													? "Fee (deducted from amount)"
													: "Fee"}
											</span>
											<div className="text-right">
												<span className="text-sm font-semibold">
													{formatCryptoAmount(fee.currencyFee, {
														ticker: fee.currency,
														maxDecimals: 6,
													})}{" "}
													<code className="text-xs">
														{splitTicker(fee.currency).token ||
															splitTicker(fee.currency).blockchain}
													</code>
												</span>
												<span className="text-xs text-muted-foreground ml-2">
													≈${fee.usdFee}
												</span>
											</div>
										</div>

										<Separator />

										<div className="flex justify-between items-center">
											<span className="text-sm font-semibold">
												Your balance is debited
											</span>
											<div className="text-right">
												<span className="text-sm font-semibold">
													{formatCryptoAmount(
														renderTotalSum(
															amountValue,
															props.balance,
															fee,
															deductFeeFromAmount,
														),
														{
															ticker: props.balance.ticker,
															maxDecimals: 6,
														},
													)}{" "}
													<code className="text-xs">{displayTicker}</code>
												</span>
												<span className="text-xs text-muted-foreground ml-2">
													≈$
													{renderTotalConvertedAmount(
														convertedAmount,
														fee,
														deductFeeFromAmount,
													)}
												</span>
											</div>
										</div>
									</div>
								</div>
							)}

							{hasInsufficientBalance && (
								<Alert variant="destructive">
									<AlertDescription className="text-xs">
										<strong>Insufficient Balance</strong> The total amount
										exceeds your available balance.
									</AlertDescription>
								</Alert>
							)}

							<Button type="submit" className="sr-only">
								Submit
							</Button>
						</>
					)}

				{!filteredAddresses.length && props.balance.id !== "empty" && (
					<div className="space-y-4">
						<div className="flex items-start gap-3 p-4 rounded-lg bg-muted/50 border border-border">
							<InfoIcon className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-0.5" />
							<div className="text-sm space-y-2">
								<p className="font-semibold">No withdrawal address available</p>
								<p className="text-muted-foreground">
									To create a withdrawal, first create an address in the
									settings section or use the button below.
								</p>
							</div>
						</div>
						<Button
							type="button"
							size="lg"
							className="w-full"
							disabled={props.isFormSubmitting}
							onClick={() => setIsAddressFormOpen(true)}
						>
							{props.isFormSubmitting ? (
								<span className="flex items-center">
									<span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
									Processing...
								</span>
							) : (
								"Create an Address"
							)}
						</Button>
					</div>
				)}
			</form>
			<DrawerForm
				title="Create an address"
				description="Add a new withdrawal address for your crypto transactions."
				isOpen={isAddressFormOpen}
				onClose={() => setIsAddressFormOpen(false)}
				onSubmit={() => {
					const addressForm = document.getElementById("address-create-form");
					if (addressForm) {
						addressForm.dispatchEvent(
							new Event("submit", { cancelable: true, bubbles: true }),
						);
					}
				}}
				isSubmitting={props.isFormSubmitting}
				submitText="Save Address"
				width={512}
			>
				<AddressCreateForm
					isFormSubmitting={props.isFormSubmitting}
					onFinish={uploadCreatedAddress}
				/>
			</DrawerForm>
		</>
	);
});

WithdrawForm.displayName = "WithdrawForm";

export default WithdrawForm;
